/**
 * HorizontalZonesPrimitive — fills shaded horizontal price bands (zones) across
 * the full visible width of the price pane, between two prices (top/bottom),
 * with an optional inline label.
 *
 * Used by the options payoff-zones overlay to shade the profit zone (between
 * break-evens), the max-profit band, and the −50%-of-investment danger zone on
 * the underlying chart — so the strategy's economics read directly off spot.
 *
 * Unlike BandPrimitive (time-series upper/lower envelopes) these are flat
 * horizontal regions independent of bar geometry.
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface HZone {
  /** Upper price bound of the band. */
  top: number;
  /** Lower price bound of the band. */
  bottom: number;
  /** CSS rgba fill (include alpha, e.g. 'rgba(38,166,154,0.12)'). */
  fill: string;
  /** Optional border colour for the top/bottom edges. */
  edge?: string;
  /** Optional inline label drawn at the right edge inside the band. */
  label?: string;
  /** Label text colour (defaults to edge). */
  labelColor?: string;
}

class HZonesRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly zones: HZone[],
    private readonly series: ISeriesApi<SeriesType>,
  ) {}

  draw(target: CanvasRenderingTarget2D): void {
    if (!this.zones.length) return;
    const series = this.series;
    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      ctx.save();
      const rightX = bitmapSize.width;
      for (const z of this.zones) {
        const yTopC = series.priceToCoordinate(z.top);
        const yBotC = series.priceToCoordinate(z.bottom);
        if (yTopC == null || yBotC == null) continue;
        let yTop = Number(yTopC) * vpr;
        let yBot = Number(yBotC) * vpr;
        if (yTop > yBot) { const t = yTop; yTop = yBot; yBot = t; }
        const h = Math.max(1, yBot - yTop);
        ctx.fillStyle = z.fill;
        ctx.fillRect(0, yTop, rightX, h);
        if (z.edge) {
          ctx.strokeStyle = z.edge;
          ctx.lineWidth = Math.max(1, Math.min(hpr, vpr));
          ctx.setLineDash([4 * hpr, 3 * hpr]);
          ctx.beginPath(); ctx.moveTo(0, yTop); ctx.lineTo(rightX, yTop); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(0, yBot); ctx.lineTo(rightX, yBot); ctx.stroke();
          ctx.setLineDash([]);
        }
        if (z.label) {
          const fontPx = Math.round(10 * Math.min(hpr, vpr));
          ctx.font = `${fontPx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
          ctx.fillStyle = z.labelColor || z.edge || 'rgba(160,160,160,0.9)';
          ctx.textBaseline = 'top';
          // LEFT edge — the right side is occupied by the price axis + option-profile
          // rail, which was hiding the profit/loss labels.
          ctx.textAlign = 'left';
          ctx.fillText(z.label, 6 * hpr, yTop + 3 * vpr);
        }
      }
      ctx.restore();
    });
  }
}

class HZonesPaneView implements IPrimitivePaneView {
  constructor(private readonly parent: HorizontalZonesPrimitive) {}
  zOrder() { return 'bottom' as const; } // behind candles so price action stays readable
  renderer(): IPrimitivePaneRenderer | null {
    const series = this.parent.series;
    if (!series) return null;
    return new HZonesRenderer(this.parent.zones, series);
  }
}

export class HorizontalZonesPrimitive implements ISeriesPrimitive<Time> {
  zones: HZone[] = [];
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _paneViews: HZonesPaneView[];

  constructor(zones: HZone[]) {
    this.zones = zones;
    this._paneViews = [new HZonesPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void { this.chart = null; this.series = null; }

  updateAllViews(): void { /* renderer reads this.zones fresh each draw */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(zones: HZone[]): void {
    this.zones = zones;
    this.chart?.applyOptions({});
  }
}
